import { useState, useCallback, useMemo } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import { breakpoints, useWindowWidth } from '@frontend/responsiveness';
import { useSoftphoneEventSubscription } from './softphone-events-provider';

type WidgetControlStateValue = {
  isOpen: boolean;
  isMobile: boolean;
  isPhone: boolean;
  isFeedbackOpen: boolean;
};

type WidgetControlActionsValue = {
  open: () => void;
  close: () => void;
  setIsFeedbackOpen: (val: boolean) => void;
};

type WidgetControlContextValue = WidgetControlStateValue & WidgetControlActionsValue;

const WidgetControlStateContext = createContext({} as WidgetControlStateValue);
const WidgetControlActionsContext = createContext({} as WidgetControlActionsValue);

type Props = {
  mobileBreakpoint?: number;
  children: React.ReactNode;
};
export const SoftphoneWidgetControlProvider = ({ mobileBreakpoint, children }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const windowWidth = useWindowWidth({ tolerance: 5 });
  const isMobile = useMemo(
    () => windowWidth <= (mobileBreakpoint ?? breakpoints.small.max),
    [windowWidth, mobileBreakpoint]
  );
  const isPhone = useMemo(() => windowWidth <= breakpoints.xsmall.max, [windowWidth]);

  /**
   * close softphone when call is initated but remain open if isOpen is true after the call is initated
   */

  useSoftphoneEventSubscription(['blind-transfer.sent', 'attended-transfer.after-established.transferred'], close, [
    close,
  ]);

  const stateValue = useMemo(
    () => ({
      isOpen,
      isMobile,
      isPhone,
      isFeedbackOpen,
    }),
    [isOpen, isMobile, isPhone, isFeedbackOpen]
  ) satisfies WidgetControlStateValue;

  const actionsValue = useMemo(
    () => ({
      open,
      close,
      setIsFeedbackOpen,
    }),
    [open, close]
  ) satisfies WidgetControlActionsValue;

  return (
    <WidgetControlStateContext.Provider value={stateValue}>
      <WidgetControlActionsContext.Provider value={actionsValue}>{children}</WidgetControlActionsContext.Provider>
    </WidgetControlStateContext.Provider>
  );
};

// Hook for accessing state values (isOpen, isMobile, isPhone, isFeedbackOpen)
export const useSoftphoneWidgetState = <T extends any>(selector: (value: WidgetControlStateValue) => T) => {
  return useContextSelector(WidgetControlStateContext, selector);
};

// Hook for accessing action functions (open, close, setIsFeedbackOpen)
export const useSoftphoneWidgetActions = <T extends any>(selector: (value: WidgetControlActionsValue) => T) => {
  return useContextSelector(WidgetControlActionsContext, selector);
};

// Combined hook for backward compatibility - combines both state and actions
export const useSoftphoneWidgetControl = <T extends any>(selector: (value: WidgetControlContextValue) => T) => {
  const state = useContextSelector(WidgetControlStateContext, (s) => s);
  const actions = useContextSelector(WidgetControlActionsContext, (a) => a);

  const combinedValue = useMemo(() => ({ ...state, ...actions }), [state, actions]);

  return useMemo(() => selector(combinedValue), [selector, combinedValue]);
};
